import {
  TemplateUsageJSON,
  TemplateUsageSide,
  TemplateUsageStage,
} from "work/template-usages/_schemas/template-usage-schema";

const stageLabels: Readonly<Record<TemplateUsageStage, string>> = {
  [TemplateUsageStage.DraftProposal]: "Draft proposal",
  [TemplateUsageStage.ActiveProposal]: "Proposal",
  [TemplateUsageStage.ArchivedProposal]: "Archived proposal",
  [TemplateUsageStage.ActiveWork]: "Active work",
  [TemplateUsageStage.ArchivedWork]: "Archived work",
};

/** The counterparties a reader is shown under one heading, in the order they are listed. */
export type TemplateUsageGroup = {
  key: string;
  counterparty: string;
  usages: TemplateUsage[];
};

/**
 * One proposal or work built from a template, and who it is with (#2401).
 *
 * The counterparty is the firm on the other side from the reader. A client's templates of the vendor
 * team it hires sit in the vendor's list, so which list names the template does not say who that
 * is; the API works it out and says which party it is. The API leaves it unnamed when the reader may not see the other side's team, and a
 * draft may not have a vendor yet; both are said so rather than shown blank.
 */
export default class TemplateUsage {
  public static fromJSON(json: TemplateUsageJSON): TemplateUsage {
    return new TemplateUsage(json);
  }

  /**
   * The usages under their counterparties, counterparties in name order and the unnamed last.
   * The API sends the rows in name order, which each group keeps.
   */
  public static groupByCounterparty(usages: TemplateUsage[]): TemplateUsageGroup[] {
    const groups = new Map<string, TemplateUsageGroup>();
    for (const usage of usages) {
      const key = usage.counterpartyKey;
      const group = groups.get(key) ?? { key, counterparty: usage.counterpartyLabel, usages: [] };
      group.usages.push(usage);
      groups.set(key, group);
    }
    return [...groups.values()].sort((a, b) => {
      const aNamed = a.key.startsWith("entity:");
      const bNamed = b.key.startsWith("entity:");
      if (aNamed !== bNamed) return aNamed ? -1 : 1;
      return a.counterparty.localeCompare(b.counterparty);
    });
  }

  public readonly workAgreementId: string;
  public readonly proposalId: string;
  public readonly name: string;
  public readonly stage: TemplateUsageStage;
  public readonly templateSide: TemplateUsageSide;
  public readonly counterpartySide: TemplateUsageSide;
  public readonly counterpartyIsMasked: boolean;
  public readonly counterpartyLegalEntityId?: string;
  public readonly counterpartyName?: string;

  /** What the stage is called on the template pages. */
  public get stageLabel(): string {
    return stageLabels[this.stage];
  }

  /** The counterparty as the reader sees it: a name, or why there is none. */
  public get counterpartyLabel(): string {
    if (this.counterpartyIsMasked) return "Not shared with you";
    if (this.counterpartyName) return this.counterpartyName;
    if (this.counterpartyLegalEntityId) return "Unnamed organization";
    return this.counterpartySide === TemplateUsageSide.Vendor ? "No vendor yet" : "No client yet";
  }

  /** The page the usage opens: a work agreement by its id, a proposal by the proposal's. */
  public get href(): string {
    switch (this.stage) {
      case TemplateUsageStage.ActiveWork:
        return `/work/active/view/${this.workAgreementId}/details`;
      case TemplateUsageStage.ArchivedWork:
        return `/work/archived/view/${this.workAgreementId}/details`;
      case TemplateUsageStage.DraftProposal:
        return `/proposals/drafts/view/${this.proposalId}/details`;
      case TemplateUsageStage.ArchivedProposal:
        return `/proposals/archived/view/${this.proposalId}/details`;
      default:
        return `/proposals/active/view/${this.proposalId}/details`;
    }
  }

  private constructor(json: TemplateUsageJSON) {
    this.workAgreementId = json.workAgreementId;
    this.proposalId = json.proposalId;
    this.name = json.name;
    this.stage = json.stage;
    this.templateSide = json.templateSide;
    this.counterpartySide = json.counterparty.side;
    this.counterpartyIsMasked = json.counterparty.isMasked;
    this.counterpartyLegalEntityId = json.counterparty.legalEntityId ?? undefined;
    this.counterpartyName = json.counterparty.name ?? undefined;
  }

  private get counterpartyKey(): string {
    if (this.counterpartyIsMasked) return "masked";
    if (this.counterpartyLegalEntityId) return `entity:${this.counterpartyLegalEntityId}`;
    // Per side: "No vendor yet" and "No client yet" are different headings, and a template used on
    // both sides would otherwise file one side's rows under the other's label.
    return `none:${this.counterpartySide}`;
  }
}
